import type { PDFPageProxy, RenderTask } from "pdfjs-dist"
import * as React from "react"

import { getPdfDocument } from "../pdf/pdf-loader"

/**
 * One page of the printed label rendered to a canvas, shared by the
 * checklist's focused label and the document view.
 *
 * The page is rasterised at a multiple of its on-screen size and then zoomed
 * by CSS, so zooming and reframing animate as a transform instead of stalling
 * on a re-render, and still read sharply at the zooms each view uses.
 */

export interface PageGeometry {
  /** The page's laid-out size in CSS pixels, at zoom 1. */
  width: number
  height: number
  /** Its native size in PDF points — the divisor for every box. */
  pointWidth: number
  pointHeight: number
  /** The page actually rendered, 1-based; may differ from the one asked for. */
  page: number
  /** How many pages the document has. */
  pageCount: number
}

/** Kept below iOS Safari's ~16.7M-pixel canvas limit, with headroom. */
const MAX_CANVAS_PIXELS = 12_000_000

export function clamp(value: number, lo: number, hi: number): number {
  return Math.min(Math.max(value, lo), hi)
}

export function useElementSize(ref: React.RefObject<HTMLElement | null>): {
  width: number
  height: number
} {
  const [size, setSize] = React.useState({ width: 0, height: 0 })
  React.useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = (): void => {
      setSize({ width: el.clientWidth, height: el.clientHeight })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => {
      observer.disconnect()
    }
  }, [ref])
  return size
}

/** Render one page (1-based) of the document into `canvasRef`, sized to `width` CSS pixels. */
export function usePdfPage(
  documentUrl: string,
  page: number,
  width: number,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  rasterScale = 2,
): { geometry: PageGeometry | null; loading: boolean; failed: boolean } {
  const [geometry, setGeometry] = React.useState<PageGeometry | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    if (!documentUrl || width <= 0) return
    // Read through a function: the flag flips during the awaits, which a plain
    // variable check would narrow away.
    const run = { cancelled: false }
    const isCancelled = (): boolean => run.cancelled
    let renderTask: RenderTask | null = null
    let renderedPage: PDFPageProxy | null = null
    setLoading(true)
    setFailed(false)

    void (async () => {
      try {
        const doc = await getPdfDocument(documentUrl)
        if (isCancelled()) return
        // A finding can name a page this file does not have — a stale record,
        // or a replaced upload. Show the nearest real page rather than nothing.
        const target = clamp(page, 1, doc.numPages)
        const pdfPage = await doc.getPage(target)
        if (isCancelled()) return
        renderedPage = pdfPage

        const base = pdfPage.getViewport({ scale: 1 })
        const cssScale = width / base.width
        // The multiple is capped by the canvas's pixel count, not just its factor: a wide layout
        // or a large-format label at 3x passes iOS Safari's ~16.7M-pixel canvas limit, and a
        // canvas over it renders blank. Held twice (off-screen + live), so the cap leaves room.
        const cssPixels = base.width * cssScale * base.height * cssScale
        const scale = Math.min(rasterScale, Math.sqrt(MAX_CANVAS_PIXELS / cssPixels))
        const viewport = pdfPage.getViewport({ scale: cssScale * scale })
        const canvas = canvasRef.current
        const ctx = canvas?.getContext("2d")
        if (!canvas || !ctx) return

        // Render off-screen and swap the finished frame in: resizing the live
        // canvas clears it, and the page would blank on every change.
        const offscreen = document.createElement("canvas")
        offscreen.width = viewport.width
        offscreen.height = viewport.height
        const offscreenCtx = offscreen.getContext("2d")
        if (!offscreenCtx) return
        renderTask = pdfPage.render({ canvas: offscreen, canvasContext: offscreenCtx, viewport })
        await renderTask.promise
        if (isCancelled()) return

        canvas.width = viewport.width
        canvas.height = viewport.height
        ctx.drawImage(offscreen, 0, 0)
        setGeometry({
          width,
          height: base.height * cssScale,
          pointWidth: base.width,
          pointHeight: base.height,
          page: target,
          pageCount: doc.numPages,
        })
        setLoading(false)
      } catch (cause) {
        if (isCancelled()) return
        if (cause instanceof Error && cause.name === "RenderingCancelledException") return
        setFailed(true)
        setLoading(false)
      }
    })()

    return () => {
      run.cancelled = true
      renderTask?.cancel()
      renderedPage?.cleanup()
    }
  }, [documentUrl, page, width, canvasRef, rasterScale])

  return { geometry, loading, failed }
}
